var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { Exclude, Expose, Type } from 'class-transformer';
import { IsArray, IsBoolean, IsEnum, IsNumber, IsOptional, IsString } from 'class-validator';
import dayjs from 'dayjs';
import { jwtDecode } from 'jwt-decode';
import { SubscriptionStatus } from '../payment/index.js';
import { PayedFeatureType } from '../payment/payment-feature-type.enum.js';
import { AuthUserDto } from './auth-user.dto.js';
import { CaregiverContextDto } from './caregiver-context.js';
import { deriveSecondFactor, SecondFactorType } from './second-factor.js';
export var AuthContextType;
(function (AuthContextType) {
    AuthContextType["NONE"] = "NONE";
    AuthContextType["USER"] = "USER";
    AuthContextType["SYSTEM"] = "SYSTEM";
})(AuthContextType || (AuthContextType = {}));
export class AuthContext {
    type = AuthContextType.NONE;
    public = false;
    user = new AuthUserDto();
    /**
     * The caregiver entry the request acts through (#1841); `user` is then the
     * patient's Person. Null or absent when the request acts as the Account's own
     * Person. Travels in every Bull snapshot of the context as plain JSON.
     */
    caregiver;
    fromTan;
    tanCallId;
    accessibleWithTan;
    accessibleWithoutOnboarding;
    overrideTan;
    payedFeatureTypes;
    subscriptionStatus;
    acr;
    /**
     * What ran at this login, per RFC 8176 (TM-9636 / #1626).
     *
     * Passed on raw so that a later question can ask it without fetching the
     * token again. The decision is made over the two fields below.
     */
    amr;
    /**
     * When the second factor was given, ISO 8601; `null` when none ran
     * (TM-9636 / #1626, #1598).
     *
     * ⚠ **Normalised and not `acr`.** What counts stands in
     * `deriveSecondFactor`: TOTP and passkey yes, email and SMS OTP never. A
     * check on `acr >= 2` would instead hang on the `acr.loa.map` map on the
     * Keycloak client and change its meaning when somebody changes the map.
     *
     * ⚠ **A string, not a `Date`.** The context travels as JSON through the Bull
     * queue; a `Date` would come back out of it as a string while the type kept
     * promising `Date`.
     */
    secondFactorAt;
    /** Which factor it was — `null` when none ran. */
    secondFactorType;
    /**
     * When this person last really logged in, ISO 8601 — `auth_time` from the
     * token, `null` when it is missing.
     *
     * ⚠ **The old moment after a cookie login too.** The email change stands on
     * it (`EMAIL_CHANGE_REAUTH_WINDOW_SECONDS`); since #1626 `acr` no longer
     * tells a password entry apart from a reload. A string for the same reason as
     * `secondFactorAt`.
     */
    authenticatedAt;
    apikeyCapabilities;
    fromApikey;
    usedForm;
    /**
     * The raw token string used to create this auth context
     * Not always present, leaving out ? to avoid issues with tests
     */
    token;
    /**
     * Create a system-level auth context for background/internal operations
     * that are not initiated by a user (e.g. Keycloak sync, processors).
     * Grants full permissions - use only for trusted system operations.
     */
    static system() {
        const authContext = new AuthContext();
        authContext.type = AuthContextType.SYSTEM;
        return authContext;
    }
    static fromKeycloakToken(token) {
        const decodedToken = AuthContext.decodeKeycloakToken(token);
        return AuthContext.fromParsedKeycloakToken(token, decodedToken);
    }
    static fromParsedKeycloakToken(token, tokenParsed) {
        const authContext = new AuthContext();
        authContext.type = AuthContextType.USER;
        // No Person id, role or onboarded flag: an Account has one Person per office,
        // and which one this tab acts as comes from `/users/me` in its Active office
        // (#1822). The token carries nothing that belongs to an office (#1823).
        authContext.user.keycloakId = tokenParsed.sub;
        authContext.user.email = tokenParsed.email;
        authContext.user.firstName = tokenParsed.given_name;
        authContext.user.lastName = tokenParsed.family_name;
        authContext.user.officeId = null;
        // A Keycloak login is always a password login: the TAN left Keycloak (#1858),
        // and a TAN tab is built by `fromCallSession`.
        authContext.usedForm = 'username-password';
        authContext.token = token;
        authContext.acr = tokenParsed.acr ? Number(tokenParsed.acr) : null;
        AuthContext.setAuthenticationEvidence(authContext, tokenParsed);
        return authContext;
    }
    /**
     * The context of a TAN tab (#1856), before `/users/me` fills in the Person.
     * Same shape as a TAN login always had (`usedForm = 'tan'`, `tanCallId`), so
     * the app's TAN checks hold; the Person is the call's patient, fixed by the
     * token. No Account behind it: no `keycloakId`, no second factor.
     */
    static fromCallSession(session) {
        const authContext = new AuthContext();
        authContext.type = AuthContextType.USER;
        authContext.user.id = session.patientId;
        authContext.user.officeId = null;
        authContext.usedForm = 'tan';
        authContext.fromTan = true;
        authContext.tanCallId = session.callId;
        authContext.token = session.token;
        authContext.acr = null;
        authContext.amr = null;
        authContext.secondFactorAt = null;
        authContext.secondFactorType = null;
        authContext.authenticatedAt = null;
        return authContext;
    }
    /**
     * Record second factor and login moment from the token claims
     * (TM-9636 / #1626).
     *
     * ⚠ **One place for both edges.** The frontend builds the context through
     * {@link AuthContext.fromParsedKeycloakToken}, the backend in
     * `GetAuthContextHandler` out of the same token. A second derivation would be
     * a second answer to *"has this person given a second factor"*, and the milder
     * of the two would win.
     */
    static setAuthenticationEvidence(authContext, tokenParsed) {
        const evidence = deriveSecondFactor(tokenParsed);
        authContext.amr = tokenParsed.amr ?? null;
        authContext.secondFactorAt = evidence?.at ?? null;
        authContext.secondFactorType = evidence?.type ?? null;
        const authTime = tokenParsed.auth_time;
        authContext.authenticatedAt =
            typeof authTime === 'number' && Number.isFinite(authTime)
                ? new Date(authTime * 1000).toISOString()
                : null;
        return authContext;
    }
    static setCurrentOfficeLocation(authContext, officeLocation) {
        authContext.user.officeId = officeLocation.officeId;
        authContext.user.currentOfficeLocation = officeLocation;
        authContext.user.currentOfficeLocationId = officeLocation.id;
        const subscriptions = authContext.user.currentOfficeLocation?.subscriptions;
        if (!subscriptions ||
            subscriptions.length === 0 ||
            subscriptions.every((s) => dayjs(s.endingOn).isBefore(dayjs()))) {
            authContext.subscriptionStatus = SubscriptionStatus.incomplete;
        }
        else {
            authContext.subscriptionStatus = subscriptions[0].status;
        }
        return authContext;
    }
    /**
     * The Account the request was made with (ADR-0042): the caregiver's own in a
     * caregiver context, else the subject Person's. Account-level acts (push
     * subscriptions, login address, password, second factor) apply to this one.
     */
    static actingAccountId(authContext) {
        return authContext.caregiver?.accountId ?? authContext.user?.keycloakId ?? null;
    }
    static overrideUser(authContext, user) {
        authContext.user = user;
        return authContext;
    }
    static decodeKeycloakToken(token) {
        try {
            const decoded = jwtDecode(token);
            if (!decoded || typeof decoded !== 'object') {
                throw new Error('Invalid token format.');
            }
            return decoded;
        }
        catch {
            throw new Error('Invalid token format.');
        }
    }
}
__decorate([
    Expose(),
    IsEnum(AuthContextType),
    __metadata("design:type", String)
], AuthContext.prototype, "type", void 0);
__decorate([
    Expose(),
    IsBoolean(),
    IsOptional(),
    __metadata("design:type", Boolean)
], AuthContext.prototype, "public", void 0);
__decorate([
    Expose(),
    Type(() => AuthUserDto),
    __metadata("design:type", AuthUserDto)
], AuthContext.prototype, "user", void 0);
__decorate([
    Expose(),
    IsOptional(),
    Type(() => CaregiverContextDto),
    __metadata("design:type", Object)
], AuthContext.prototype, "caregiver", void 0);
__decorate([
    Expose(),
    IsBoolean(),
    IsOptional(),
    __metadata("design:type", Boolean)
], AuthContext.prototype, "fromTan", void 0);
__decorate([
    Expose(),
    IsBoolean(),
    IsOptional(),
    __metadata("design:type", String)
], AuthContext.prototype, "tanCallId", void 0);
__decorate([
    Expose(),
    IsBoolean(),
    IsOptional(),
    __metadata("design:type", Boolean)
], AuthContext.prototype, "accessibleWithTan", void 0);
__decorate([
    Expose(),
    IsBoolean(),
    IsOptional(),
    __metadata("design:type", Boolean)
], AuthContext.prototype, "accessibleWithoutOnboarding", void 0);
__decorate([
    Expose(),
    IsBoolean(),
    IsOptional(),
    __metadata("design:type", Boolean)
], AuthContext.prototype, "overrideTan", void 0);
__decorate([
    IsArray(),
    IsEnum(PayedFeatureType, { each: true }),
    Expose(),
    __metadata("design:type", Array)
], AuthContext.prototype, "payedFeatureTypes", void 0);
__decorate([
    IsEnum(SubscriptionStatus),
    Expose(),
    __metadata("design:type", String)
], AuthContext.prototype, "subscriptionStatus", void 0);
__decorate([
    IsNumber(),
    IsOptional(),
    __metadata("design:type", Object)
], AuthContext.prototype, "acr", void 0);
__decorate([
    Expose(),
    IsArray(),
    IsOptional(),
    __metadata("design:type", Object)
], AuthContext.prototype, "amr", void 0);
__decorate([
    Expose(),
    IsString(),
    IsOptional(),
    __metadata("design:type", Object)
], AuthContext.prototype, "secondFactorAt", void 0);
__decorate([
    Expose(),
    IsEnum(SecondFactorType),
    IsOptional(),
    __metadata("design:type", Object)
], AuthContext.prototype, "secondFactorType", void 0);
__decorate([
    Expose(),
    IsString(),
    IsOptional(),
    __metadata("design:type", Object)
], AuthContext.prototype, "authenticatedAt", void 0);
__decorate([
    Expose(),
    IsArray(),
    IsOptional(),
    __metadata("design:type", Array)
], AuthContext.prototype, "apikeyCapabilities", void 0);
__decorate([
    Expose(),
    IsBoolean(),
    IsOptional(),
    __metadata("design:type", Boolean)
], AuthContext.prototype, "fromApikey", void 0);
__decorate([
    Expose(),
    IsString(),
    IsOptional(),
    __metadata("design:type", String)
], AuthContext.prototype, "usedForm", void 0);
__decorate([
    Exclude(),
    IsOptional(),
    IsString(),
    __metadata("design:type", String)
], AuthContext.prototype, "token", void 0);
//# sourceMappingURL=auth-context.js.map